import * as Yup from "yup"
import { validationMessages } from "../common/validationMessages";

export interface ShipmentWorkItem {
    id: number;
    type: ShipmentWorkItemType;
    probillNumber: string;
    terminalNumber: number;
    terminalName: string;
    terminalAirportcode: string;
    billDate: string;
    etaDate: string;
    payorNumber: number;
    payorName: string;
    shipperNumber: number;
    shipperName: string;
    consigneeNumber: number;
    consigneeName: string;
    probillStatus: string;
    serviceStatus: ShipmentWorkItemServiceStatus;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
};

export enum ShipmentWorkItemType {
    DELIVERY = "DELIVERY",
    PICKUP = "PICKUP"

}

export enum ShipmentWorkItemReviewStatus {
    NOT_REQUIRED = "NOT_REQUIRED",
    PENDING = "PENDING",
    REVIEWED = "REVIEWED"
}

export enum ShipmentWorkItemServiceStatus {
    DUE = "DUE",
    IN_PROGRESS = "IN_PROGRESS",
    COMPLETED = "COMPLETED",
    FAILED = "FAILED"
}

export interface OperationsReviewSummary {
    totalShipmentWorkItems: number;
    pendingOperationsReview: number;
    compltedOperationsReview: number;
    pendingAppliedRate: number;
    acceptedAppliedRate: number;
    rejectedAppliedRate: number;
};

export interface OperationsReviewSummaryResponse extends OperationsReviewSummary {
    message?: string;
}

export interface OperationsReviewWorkItem extends ShipmentWorkItem {
    operationsReviewStatus: ShipmentWorkItemReviewStatus;
    accessorials: ShipmentWorkItemAccessorial[]
}

export interface OperationsReviewWorkItemFilters {
    probillNumber?: number | string | null;
    shipperName?: string | null;
    consigneeName?: string | null;
    etaStartDate?: string;
    etaEndDate?: string;
};

export interface OperationsReviewWorkItemRequest extends OperationsReviewWorkItemFilters {
    terminalNumber: number;
    operationsReviewStatus?: number | string | null;
    cursor?: number;
};

export interface OperationsReviewListResponse {
    shipmentWorkItems?: OperationsReviewWorkItem[];
    nextCursor: number;
    message?: string;
};

export interface ShipmentWorkItemAccessorial {
    id: string;
    appliedRate: string;
    applicationReason: string;
    operationApprovalStatus: AccessorialApprovalStatus;
    operationNotes?: string;
    operationApprovedAt: string;
    operationApprovedBy: string;
    billingRate: string;
    billingApprovalStatus: AccessorialApprovalStatus;
    billingNotes?: string;
    billingApprovedAt: string;
    billingApprovedBy: string;
    accessorialDefinitionId: string;
    shipmentWorkItemId: number;
    createdAt: string;
    createdBy: string;
    updatedAt?: string;
    updatedBy?: string;
    accessorialDefinition: AccessorialDefinition;
    detailKind?: AccessorialDetailKind | string | null;
    details?: AccessorialDetentionDetailsStored | Record<string, unknown> | null;
}

export enum AccessorialDetailKind {
    DETENTION = "DETENTION",
}

export interface AccessorialDetentionDetailsStored {
    schemaVersion: number;
    deliveryManifestNumber: number;
    deliveryManifestStopNumber: number;
    driverId: string;
    driverName: string;
    probillWeight: number;
    stopWeight: number;
    stopArrivedAt: string;
    stopDepartedAt: string;
    stopDurationMinutes: number;
    stopProbillNumbers: number[];
    /** Present on newer API payloads; UI falls back to parsed applicationReason / heuristics when absent. */
    detentionCategory?: AccessorialDetentionCategory;
    stopTimesSource?: AccessorialDetentionStopTimesSource;
}

export interface AccessorialDetentionCategory {
    freeTimeMinutes: number;
    maxWeight: number;
    minWeight: number;
}

export enum AccessorialDetentionStopTimesSource {
    MANUAL_POD = 'MANUAL_POD',
    ONEXGS_DRIVER_APP = 'ONEXGS_DRIVER_APP',
    SAMSARA = 'SAMSARA',
    OTHER = 'OTHER',
}

export interface AccessorialDefinition {
    id: string;
    name: string;
    rate: string;
    commodityCode: string;
    isActive: boolean;
}

export interface ShipmentWorkItemAccessorialsRequest {
    isOperationsReviewRequired?: boolean;
    isBillingReviewRequired?: boolean;
}

export interface ShipmentWorkItemAccessorialsResponse {
    accessorials: ShipmentWorkItemAccessorial[];
    message?: string;
}

export enum AccessorialApprovalStatus {
    NOT_REQUIRED = "NOT_REQUIRED",
    PENDING = "PENDING",
    ACCEPTED = "ACCEPTED",
    RJECTED = "REJECTED"
}

export const ShipmentWorkItemServiceStatusOptions = [
    { label: "Due", value: "DUE" },
    { label: "In Progress", value: "IN_PROGRESS" },
    { label: "Completed", value: "COMPLETED" },
    { label: "Failed", value: "FAILED" }
];

export enum ShipmentReconciliationSummaryTileTypes {
    "HEADING" = "HEADING",
    "INFO" = "INFO",
    "PROGRESS" = "PROGRESS"
}

export enum ShipmentReconciliationSummaryTypes {
    "TOTAL_SHIPMENTS" = "TOTAL_SHIPMENTS",
    "PENDING_FOR_REVIEW" = "PENDING_FOR_REVIEW",
    "REVIEWED" = "REVIEWED",
    "REVIEW_PROGRESS" = "REVIEW_PROGRESS",
    "ACCEPTED" = "ACCEPTED",
    "REJECTED" = "REJECTED",
    "APPROVAL_RATE" = "APPROVAL_RATE",
    "REVIEW_DAYS" = "REVIEW_DAYS",
    "FINDINGS" = "FINDINGS",
    "REVENUE" = "REVENUE",
    "AVERAGE" = "AVERAGE"
}

export const ShipmentWorkItemServiceStatusMap = {
    "DUE": "Due",
    "IN_PROGRESS": "In Progress",
    "COMPLETED": "Completed",
    "FAILED": "Failed"
}

export const OperationsReviewFiltersSchema: Yup.ObjectSchema<OperationsReviewWorkItemFilters> = Yup.object().shape({
    probillNumber: Yup.number()
        .typeError("Probill must be a number")
        .max(99999999, "The value must be less then 8 digits")
        .nullable(),
    etaStartDate: Yup.string(),
    etaEndDate: Yup.string(),
    serviceStatus: Yup.string().nullable(),
    shipperName: Yup.string().nullable(),
    consigneeName: Yup.string().nullable(),
}).defined();

export interface OperationsAccessorialApprovalRequest {
    approvalStatus: string;
    notes?: string;
}

export interface OperationsAccessorialApprovalResponse {
    message?: string;
}

export const OperationsAccessorialApprovalRequestValidationSchema: Yup.ObjectSchema<OperationsAccessorialApprovalRequest> = Yup.object().shape({
    approvalStatus: Yup.string().required(validationMessages.required),
    notes: Yup.string()
        .test("operation-notes-required", validationMessages.required, function (value) {
            let approvalStatus = this.parent.approvalStatus;
            if (approvalStatus === 'REJECTED') {
                return !!value;
            } else {
                return true;
            }
        })
}).defined();

export interface BillingReviewSummary {
    totalShipmentWorkItems: number;
    pendingBillingReview: number;
    compltedBillingReview: number;
};

export interface BillingReviewSummaryResponse extends BillingReviewSummary {
    message?: string;
}

export interface BillingReviewWorkItemFilters {
    probillNumber?: number | string | null;
    shipperName?: string | null;
    consigneeName?: string | null;
    etaStartDate?: string;
    etaEndDate?: string;
};

export interface BillingReviewWorkItemRequest extends BillingReviewWorkItemFilters {
    billingReviewStatus?: number | string | null;
    cursor?: number;
};

export interface BillingReviewWorkItem extends ShipmentWorkItem {
    billingReviewStatus: ShipmentWorkItemReviewStatus;
    accessorials: ShipmentWorkItemAccessorial[]
}

export interface BillingReviewListResponse {
    shipmentWorkItems?: BillingReviewWorkItem[];
    nextCursor: number;
    message?: string;
};

export const BillingReviewFiltersSchema: Yup.ObjectSchema<BillingReviewWorkItemFilters> = Yup.object().shape({
    probillNumber: Yup.number()
        .typeError("Probill must be a number")
        .max(99999999, "The value must be less then 8 digits")
        .nullable(),
    etaStartDate: Yup.string(),
    etaEndDate: Yup.string(),
    serviceStatus: Yup.string().nullable(),
    shipperName: Yup.string().nullable(),
    consigneeName: Yup.string().nullable(),
}).defined();

export interface BillingAccessorialApprovalRequest {
    approvalStatus: string;
    notes?: string;
    billingRate?: number;
}

export interface BillingAccessorialApprovalResponse {
    message?: string;
}

export const BillingAccessorialApprovalRequestValidationSchema: Yup.ObjectSchema<BillingAccessorialApprovalRequest> = Yup.object().shape({
    approvalStatus: Yup.string().required(validationMessages.required),
    notes: Yup.string()
        .test("billing-notes-required", validationMessages.required, function (value) {
            let approvalStatus = this.parent.approvalStatus;
            if (approvalStatus === 'REJECTED') {
                return !!value;
            } else {
                return true;
            }
        }),
    billingRate: Yup.number()
        .typeError("Value must be a number")
        .max(9999, "The value must be less then 9999")
        .test("billing-rate-required", validationMessages.required, function (value) {
            let approvalStatus = this.parent.approvalStatus;
            if (approvalStatus === 'ACCEPTED') {
                return !!value;
            } else {
                return true;
            }
        }),
}).defined();

export interface AccessorialReviewTerminalPerformance {
    terminalName: number;
    terminalNumber: number;
    terminalAirportcode: number;
    dateAdded: number;
    accessorialId: number;
    accessorialName: number;
    operationsPending: number;
    operationsAccepted: number;
    operationsRejected: number;
    operationsExpired: number;
    billingPending: number;
    billingAccepted: number;
    billingRejected: number;
    billingExpired: number;
    acceptedAmount: number;
}

export interface AccessorialReviewTerminalPerformanceRequest {
    startDateAdded: string;
    endDateAdded: string;
    terminalNumber?: number | null;
}

export interface AccessorialReviewTerminalPerformanceResponse {
    performance: AccessorialReviewTerminalPerformance[],
    message?: string;
}

export interface AccessorialReviewPerformanceSummary {
    totalAcceptedAmount: number;
    averageAcceptedAmount: number;
    averageOperationsReviewTime: number;
    averageBillingReviewTime: number;
    operationsPending: number;
    operationsAccepted: number;
    operationsRejected: number;
    operationsExpired: number;
    billingPending: number;
    billingAccepted: number;
    billingRejected: number;
    billingExpired: number;
    message?: string;
}

export const terminalOptions = [
    { label: "ABQ - ALBUQUERQUE 170", value: "170" },
    { label: "ABE - ALLENTOWN 620", value: "620" },
    { label: "ANA - ANAHEIM 109", value: "109" },
    { label: "BWI - BALTIMORE 189", value: "189" },
    { label: "CLT - CHARLOTTE 704", value: "704" },
    { label: "CHA - CHATTANOOGA 2", value: "2" },
    { label: "ORD - CHICAGO 77", value: "77" },
    { label: "CLE - CLEVELAND 946", value: "946" },
    { label: "WOR - CLINTON 351", value: "351" },
    { label: "DFW - DALLAS 4", value: "4" },
    { label: "DAY - DAYTON 944", value: "944" },
    { label: "DEN - DENVER 106", value: "106" },
    { label: "DTW - DETROIT 625", value: "625" },
    { label: "GRR - GRAND RAPIDS 642", value: "642" },
    { label: "IAH - HOUSTON 150", value: "150" },
    { label: "IND - INDIANAPOLIS 624", value: "624" },
    { label: "JAX - JACKSONVILLE 119", value: "119" },
    { label: "SEA - KENT 384", value: "384" },
    { label: "LAL - LAKELAND 863", value: "863" },
    { label: "SDF - LOUISVILLE 156", value: "156" },
    { label: "MIA - MIAMI 305", value: "305" },
    { label: "MKE - MILWAUKEE 192", value: "192" },
    { label: "MSP - MINNEAPOLIS 197", value: "197" },
    { label: "MSY - NEW ORLEANS 504", value: "504" },
    { label: "OKC - OKLAHOMA CITY 188", value: "188" },
    { label: "PHX - PHOENIX 75", value: "75" },
    { label: "PIT - PITTSBURGH 20", value: "20" },
    { label: "PDX - PORTLAND 159", value: "159" },
    { label: "ROC - ROCHESTER 145", value: "145" },
    { label: "SAC - SACRAMENTO 37", value: "37" },
    { label: "STL - SAINT LOUIS 153", value: "153" },
    { label: "SLC - SALT LAKE CITY 47", value: "47" },
    { label: "SAT - SAN ANTONIO 17", value: "17" },
    { label: "SAN - SAN DIEGO 26", value: "26" },
    { label: "GEG - SPOKANE 51", value: "51" },
    { label: "SGF - SPRINGFIELD 611", value: "611" },
    { label: "TCY - TRACY 209", value: "209" },
    { label: "THL - TUNNEL HILL 1", value: "1" },
    { label: "YNG - YOUNGSTOWN 205", value: "205" },
]

export interface AccessorialReviewDailyTrend {
    dateAdded: number;
    scannedShipments: number;
    operationsPending: number;
    operationsAccepted: number;
    operationsRejected: number;
    billingPending: number;
    billingAccepted: number;
    billingRejected: number;
    acceptedAmount: number;
}

export interface AccessorialRevenueTrendRequest {
    startDateAdded: string;
    endDateAdded: string;
    terminalNumber?: number | null;
}

export interface AccessorialReviewDailyTrendResponse {
    trends: AccessorialReviewDailyTrend[];
    message?: string;
}

export interface AccessorialRevenueTrendIndicators {
    reviewDays: number;
    averageDalyDeliveries: number;
    scannedShipments: number;
    averageDailyScans: number;
    accessorialFindings: number;
    accessorialFindingsRate: number;
    operationPending: number;
    operationsAccepted: number;
    operationsRejected: number;
    operationsApprovalRate: number;
    billingPending: number;
    billingAccepted: number;
    billingRejected: number;
    billingApprovalRate: number;
    acceptedAmount: number;
    avaerageRevenuePerAccessorial: number;
    message?: string;
}